import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useInvalidate,
  useNotification,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import type { TagSuggestionRead, TagSuggestionStatus } from "@/types/tina";

const PROVIDER = "tina";
const PATH = "tag-suggestions";

export interface AcceptValues {
  tag_name: string;
  explanation: string;
}

/** Suggestions in one status, plus the accept and snooze calls. */
export const useTagSuggestions = (status: TagSuggestionStatus) => {
  const apiUrl = useApiUrl(PROVIDER);
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();

  const query = useCustom<TagSuggestionRead[]>({
    url: apiUrl,
    method: "get",
    dataProviderName: PROVIDER,
    config: { query: { status } },
    meta: { urlPath: PATH },
  });

  const post = (id: number, suffix: string, values?: object) =>
    mutateAsync({
      url: apiUrl,
      method: "post",
      dataProviderName: PROVIDER,
      values: values ?? {},
      meta: { urlPath: `${PATH}/${id}/${suffix}` },
    });

  /** Resolves true on success; on failure shows the server's message. */
  const accept = async (id: number, values: AcceptValues): Promise<boolean> => {
    try {
      await post(id, "accept", values);
      open?.({
        type: "success",
        message: "Tag rule created",
        description: `Created rule for "${values.tag_name}"`,
      });
      await query.query.refetch();
      await invalidate({
        resource: "tina-tag-rules",
        dataProviderName: PROVIDER,
        invalidates: ["list"],
      });
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Could not create the tag rule",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  };

  const snooze = async (id: number): Promise<boolean> => {
    try {
      await post(id, "snooze");
      open?.({ type: "success", message: "Snoozed for 90 days" });
      await query.query.refetch();
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Could not snooze the suggestion",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  };

  return {
    // Refine's useCustom yields `{}` (not undefined) while loading or after an
    // error, so `?? []` alone would hand the page an object.
    suggestions: Array.isArray(query.result?.data) ? query.result.data : [],
    isLoading: query.query.isLoading,
    accept,
    snooze,
  };
};
